import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag, Row, Col,
  Drawer, Form, message, InputNumber, Input, DatePicker,
} from 'antd'
import { ScheduleOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import NumberWithUnit from '@/shared/components/NumberWithUnit'

const { Title } = Typography
const API = ''

const STATUS_COLORS: Record<string, string> = {
  PLANNED: 'default',
  IN_PROGRESS: 'processing',
  COMPLETED: 'success',
  CANCELLED: 'error',
}

const SHIFT_COLORS: Record<string, string> = {
  A: 'blue',
  B: 'gold',
  C: 'purple',
}

export default function ProductionPlanPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [dateFilter, setDateFilter] = useState<dayjs.Dayjs | null>(dayjs())
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingPlan, setEditingPlan] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [form] = Form.useForm()

  const { data: plans, isLoading } = useQuery({
    queryKey: ['production-plans', plantId, statusFilter, dateFilter],
    queryFn: () =>
      apiClient.get(`${API}/production-plans`, {
        params: {
          plant_id: plantId,
          status: statusFilter || undefined,
          plan_date: dateFilter ? dateFilter.format('YYYY-MM-DD') : undefined,
          limit: 200,
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  // Machine master is at /masters/machines and returns a paginated
  // shape {items, total, ...}. The page below treats `machines` as an
  // array, so we extract .items here. The backend MachineResponse uses
  // renamed properties (machine_id_str / name / section) — we
  // normalise to the legacy field names this page reads.
  const { data: machines } = useQuery({
    queryKey: ['machines', plantId],
    queryFn: async () => {
      const r = await apiClient.get(`${API}/masters/machines`, { params: { size: 100 } })
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return raw.map((m: any) => ({
        id: m.id,
        machine_code: m.machine_code ?? m.machine_id_str ?? '',
        machine_name: m.machine_name ?? m.name ?? '',
        machine_type: m.machine_type ?? m.section ?? '',
        spindle_count: m.spindle_count ?? null,
        machine_status: m.machine_status ?? '',
        is_active: m.is_active ?? true,
      }))
    },
    enabled: !!plantId,
  })

  const { data: mixingPlans } = useQuery({
    queryKey: ['mixing-plans-approved', plantId],
    queryFn: () =>
      apiClient.get(`${API}/mixing-plans`, {
        params: { plant_id: plantId, status: 'APPROVED', limit: 200 },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post(`${API}/production-plans`, { ...data, plant_id: plantId }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-plans'] })
      message.success('Production plan created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`${API}/production-plans/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-plans'] })
      message.success('Plan updated')
      setDrawerOpen(false)
      setEditingPlan(null)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const openCreate = () => {
    setEditingPlan(null)
    form.resetFields()
    form.setFieldsValue({ plan_date: dayjs() })
    setDrawerOpen(true)
  }

  const openEdit = (plan: any) => {
    setEditingPlan(plan)
    form.setFieldsValue({
      ...plan,
      plan_date: plan.plan_date ? dayjs(plan.plan_date) : dayjs(),
    })
    setDrawerOpen(true)
  }

  const handleSave = () => {
    form.validateFields().then(vals => {
      const payload = {
        ...vals,
        plan_date: vals.plan_date?.format('YYYY-MM-DD'),
      }
      if (editingPlan) {
        updateMut.mutate({ id: editingPlan.id, data: payload })
      } else {
        createMut.mutate(payload)
      }
    })
  }

  const planList = Array.isArray(plans) ? plans : []
  const machineList = Array.isArray(machines) ? machines : []
  const mixingList = Array.isArray(mixingPlans) ? mixingPlans : []

  const columns = [
    {
      title: 'Plan No', dataIndex: 'plan_no', key: 'plan_no', width: 130,
      render: (v: string) => <b style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</b>,
    },
    {
      title: 'Plan Date', dataIndex: 'plan_date', key: 'plan_date', width: 110,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—',
    },
    {
      title: 'Shift', dataIndex: 'shift', key: 'shift', width: 70,
      render: (v: string) => <Tag color={SHIFT_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    { title: 'Machine No', dataIndex: 'machine_no', key: 'machine_no', width: 110 },
    {
      title: 'Count (Ne)', dataIndex: 'count_ne', key: 'count_ne', width: 90, align: 'right' as const,
      render: (v: number) => v ?? '—',
    },
    {
      title: 'Target Input (kg)', dataIndex: 'target_input_kg', key: 'target_input_kg', width: 130, align: 'right' as const,
      render: (v: number) => v != null ? Number(v).toFixed(3) : '—',
    },
    {
      title: 'Target Output (kg)', dataIndex: 'target_output_kg', key: 'target_output_kg', width: 140, align: 'right' as const,
      render: (v: number) => v != null ? Number(v).toFixed(3) : '—',
    },
    {
      title: 'Target Waste %', dataIndex: 'target_waste_pct', key: 'target_waste_pct', width: 120, align: 'right' as const,
      render: (v: number) => v != null ? `${Number(v).toFixed(1)}%` : '—',
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 120,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v?.replace('_', ' ')}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 70,
      render: (_: unknown, r: any) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <ScheduleOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Production Plan
      </Title>
      <Typography.Text type="secondary">Machine-wise, shift-wise production targets</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
          <Col>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 180 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
          <Col>
            <DatePicker
              value={dateFilter}
              onChange={v => setDateFilter(v)}
              allowClear
              placeholder="Filter by date"
            />
          </Col>
          <Col>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 140 }}
              value={statusFilter}
              onChange={v => setStatusFilter(v)}
              options={['PLANNED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'].map(s => ({ value: s, label: s.replace('_', ' ') }))}
            />
          </Col>
          <Col flex="auto" />
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Plan
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={planList}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Drawer
        title={editingPlan ? `Edit Plan: ${editingPlan.plan_no}` : 'New Production Plan'}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingPlan(null); form.resetFields() }}
        width={520}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setDrawerOpen(false); setEditingPlan(null); form.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createMut.isPending || updateMut.isPending}
                onClick={handleSave}
              >
                Save
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="plan_date" label="Plan Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="shift" label="Shift" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'A', label: 'Shift A' },
                  { value: 'B', label: 'Shift B' },
                  { value: 'C', label: 'Shift C' },
                ]} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="count_ne" label="Count (Ne)">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="count_description" label="Count Description">
                <Input placeholder="e.g. 30s Combed" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="machine_id" label="Machine">
                <Select
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  placeholder="Select machine"
                  options={machineList.map((m: any) => ({
                    value: m.id,
                    label: `${m.machine_no} — ${m.machine_name ?? m.name ?? ''}`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="machine_no" label="Machine No (manual)">
                <Input placeholder="e.g. RF-01" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="target_input_kg" label="Target Input (kg)">
                <InputNumber style={{ width: '100%' }} min={0} precision={3} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="target_output_kg" label="Target Output (kg)">
                <InputNumber style={{ width: '100%' }} min={0} precision={3} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="target_waste_pct" label="Target Waste %">
                <NumberWithUnit unit="%" style={{ width: '100%' }} min={0} max={100} precision={1} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="mixing_plan_id" label="Mixing Plan">
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder="Link mixing plan"
                  options={mixingList.map((m: any) => ({
                    value: m.id,
                    label: `${m.mx_no} — ${m.count_description ?? ''}`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
